import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Target } from "lucide-react";
import { KPICircle } from "@/components/KPICircle";

const DISPLAY_NAMES = { Koebenhavn: "København" };
const displayName = (n) => DISPLAY_NAMES[n] || n;

function fmt(n) {
  return new Intl.NumberFormat("da-DK", { maximumFractionDigits: 0 }).format(n);
}

// --- Booking bar ---
const BOOKING_LEVELS = [
  { pct: 70, label: "Accept.", color: "#EAB308" },
  { pct: 85, label: "Godt",   color: "#84CC16" },
  { pct: 95, label: "Top",    color: "#22C55E" },
];

function bookingColor(pct) {
  if (pct >= 95) return "#22C55E";
  if (pct >= 85) return "#84CC16";
  if (pct >= 70) return "#EAB308";
  if (pct >= 55) return "#F97316";
  return "#EF4444";
}

export function BookingBar({ booking_pct, booking_date, prev_booking_avg, booking_avg_period, booking_goal_period }) {
  if (booking_pct == null && booking_avg_period == null) {
    return (
      <Card className="bg-card border-border shadow-none mb-6">
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-foreground">Bookinggrad</span>
            <span className="text-2xl font-extrabold text-muted-foreground">—</span>
          </div>
          <p className="text-[13px] text-muted-foreground">Afventer data fra Podio</p>
        </CardContent>
      </Card>
    );
  }

  const goal = booking_goal_period ?? 85;
  const displayPct = booking_avg_period ?? booking_pct;
  const color = bookingColor(displayPct);
  const barWidth = Math.min(displayPct, 100);
  const diffFromGoal = Math.round((displayPct - goal) * 10) / 10;

  let trendEl = null;
  if (prev_booking_avg != null && booking_avg_period != null) {
    const diff = booking_avg_period - prev_booking_avg;
    if (Math.abs(diff) >= 0.5) {
      const up = diff > 0;
      trendEl = (
        <span className="text-[13px] font-bold px-1.5 py-0.5 rounded-full ml-2"
          style={{ background: up ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)", color: up ? "#22C55E" : "#EF4444" }}>
          {up ? "↑" : "↓"} {Math.abs(diff).toFixed(1)}%
        </span>
      );
    }
  }

  const diffColor = diffFromGoal >= 0 ? "#22C55E" : "#EF4444";
  const diffLabel = diffFromGoal >= 0
    ? `+${diffFromGoal}% over mål`
    : `${diffFromGoal}% fra mål`;

  return (
    <Card className="bg-card border-border shadow-none mb-6">
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1">
            <span className="text-sm font-bold text-foreground">Bookinggrad</span>
            {trendEl}
          </div>
          <div className="text-right">
            <span className="text-2xl font-extrabold" style={{ color }}>{Math.round(displayPct)}%</span>
            <p className="text-[12px] font-semibold mt-0.5" style={{ color: diffColor }}>{diffLabel}</p>
          </div>
        </div>
        <p className="text-[13px] text-muted-foreground mb-3">
          Gennemsnit for indeværende KPI periode · Mål {goal}%
          {prev_booking_avg != null ? ` · Forrige periode: ${prev_booking_avg}%` : ""}
        </p>

        <div className="relative w-full">
          <div className="relative h-4 mb-1">
            {BOOKING_LEVELS.map(({ pct, label, color: lc }) => (
              <span key={pct} className="absolute text-[12px] font-semibold -translate-x-1/2"
                style={{ left: `${pct}%`, color: lc, top: 0 }}>
                {pct}%
              </span>
            ))}
          </div>
          <div className="relative w-full bg-secondary rounded-full h-3 overflow-visible">
            {BOOKING_LEVELS.map(({ pct, color: lc }) => (
              <div key={pct} className="absolute top-[-4px] bottom-[-4px] w-[2px] z-10"
                style={{ left: `${pct}%`, background: lc, opacity: 0.9 }} />
            ))}
            <div className="h-full rounded-full transition-all duration-700 relative overflow-hidden"
              style={{ width: `${barWidth}%`, background: color }}>
              {displayPct >= 70 && (
                <div className="absolute inset-0 rounded-full animate-pulse" style={{ background: color, opacity: 0.3 }} />
              )}
            </div>
          </div>
          <div className="relative h-5 mt-1">
            {BOOKING_LEVELS.map(({ pct, label, color: lc }) => (
              <span key={pct} className="absolute text-[12px] -translate-x-1/2 font-medium"
                style={{ left: `${pct}%`, color: displayPct >= pct ? lc : "#52525B" }}>
                {label}
              </span>
            ))}
          </div>
        </div>

        {booking_date && (
          <p className="text-[13px] text-muted-foreground mt-1">Seneste måling: {booking_date}</p>
        )}
      </CardContent>
    </Card>
  );
}

// --- Omsætning progress bar ---
export function OmsætningBar({ data }) {
  const { total_6month, goal_6month, months_elapsed, total_months, month_labels, period_start, break_even_6month } = data;
  if (!goal_6month) return null;

  // Dagspræcis position
  let elapsedExact = months_elapsed;
  if (months_elapsed > 0 && months_elapsed < total_months && period_start) {
    const now = new Date();
    const dayOfMonth = now.getDate();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    elapsedExact = months_elapsed - 1 + dayOfMonth / daysInMonth;
  }

  const expectedSoFar = elapsedExact > 0 ? (elapsedExact / total_months) * goal_6month : 0;
  const barPct = Math.min((total_6month / goal_6month) * 100, 100);

  // "Forventet nu"-streg (tidspunkt = nu)
  const expectedNowPct = elapsedExact > 0 && elapsedExact < total_months
    ? Math.min((elapsedExact / total_months) * 100, 99)
    : null;

  // Break even-streg: brug data fra backend hvis tilgængeligt, ellers estimer som 60% af mål
  const breakEvenAmount = break_even_6month ?? (goal_6month * 0.60);
  const breakEvenPct = Math.min((breakEvenAmount / goal_6month) * 100, 99);

  let color;
  if (months_elapsed === 0) color = "#3F3F46";
  else if (total_6month >= expectedSoFar * 1.05) color = "#22C55E";
  else if (total_6month >= expectedSoFar * 0.95) color = "#EAB308";
  else color = "#EF4444";

  return (
    <Card className="bg-card border-border shadow-none mb-6">
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-bold text-foreground">Omsætning 6 mdr.</span>
        </div>
        <p className="text-[13px] text-muted-foreground mb-3">
          Mål: {fmt(goal_6month)} kr · Mål pr. md.: {fmt(Math.round(goal_6month / total_months))} kr
        </p>

        <div className="relative w-full">
          {/* Månedsnavne over baren */}
          <div className="relative h-4 mb-1">
            {Array.from({ length: total_months }).map((_, i) => {
              const leftPct = (i / total_months) * 100;
              const isPast = i < months_elapsed;
              return (
                <span
                  key={i}
                  className="absolute text-[12px] font-medium -translate-x-1/2"
                  style={{ left: `${leftPct}%`, color: isPast ? "#A1A1AA" : "#52525B" }}
                >
                  {month_labels?.[i]?.slice(0, 3) || ""}
                </span>
              );
            })}
          </div>

          <div className="relative w-full bg-secondary rounded-full h-3 overflow-visible">
            {/* Måneds-markørstreger */}
            {Array.from({ length: total_months - 1 }).map((_, i) => {
              const leftPct = ((i + 1) / total_months) * 100;
              return (
                <div
                  key={i}
                  className="absolute top-[-3px] bottom-[-3px] w-px z-10"
                  style={{ left: `${leftPct}%`, background: "rgba(128,128,128,0.25)" }}
                />
              );
            })}

            {/* Break even-streg */}
            <div
              className="absolute top-[-4px] bottom-[-4px] w-[2px] z-10"
              style={{ left: `${breakEvenPct}%`, background: "#F97316", opacity: 0.85 }}
            />

            {/* Forventet nu-streg */}
            {expectedNowPct != null && (
              <div
                className="absolute top-[-4px] bottom-[-4px] w-[2px] z-10"
                style={{ left: `${expectedNowPct}%`, background: "#A1A1AA", opacity: 0.8 }}
              />
            )}

            {/* Mål-streg */}
            <div
              className="absolute top-[-4px] bottom-[-4px] w-[2px] z-10"
              style={{ left: "99.5%", background: "#22C55E", opacity: 0.6 }}
            />

            <div
              className="h-full rounded-full transition-all duration-700 relative overflow-hidden"
              style={{ width: `${barPct}%`, background: color }}
            />
          </div>

          <div className="relative h-5 mt-1 flex justify-between text-[12px] text-muted-foreground">
            <span>0 kr</span>
            <span className="absolute -translate-x-1/2" style={{ left: `${breakEvenPct}%`, color: "#F97316" }}>
              Break even
            </span>
            {expectedNowPct != null && (
              <span className="absolute -translate-x-1/2" style={{ left: `${expectedNowPct}%`, color: "#A1A1AA" }}>
                Forventet nu
              </span>
            )}
            <span style={{ color: "#22C55E" }}>Mål: {fmt(Math.round(goal_6month / 1000))}k</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// --- Weighted overall KPI score ---
// Vægte: Omsætning 38%, Booking 18%, Tilfredshed 13%, Kr/time 18%, Fremmøde 13%
export function calcOverallScore(data) {
  const {
    total_6month, goal_6month, months_elapsed, total_months,
    satisfaction_score,
    revenue_per_hour,
    avg_fravær_pct,
    booking_avg_period, booking_pct, booking_goal_period,
  } = data;

  const scores = [];

  // Omsætning (38%)
  if (goal_6month > 0 && months_elapsed > 0) {
    const expected = (months_elapsed / total_months) * goal_6month;
    const s = Math.min((total_6month / expected) * 100, 130);
    scores.push({ key: "omsætning", weight: 0.38, score: s });
  }

  // Bookinggrad (18%)
  const bookingVal = booking_avg_period ?? booking_pct;
  const bookingGoal = booking_goal_period ?? 85;
  if (bookingVal != null) {
    const s = Math.min((bookingVal / bookingGoal) * 100, 130);
    scores.push({ key: "booking", weight: 0.18, score: s });
  }

  // Elevtilfredshed (13%)
  if (satisfaction_score != null) {
    const s = Math.min((satisfaction_score / 80) * 100, 130);
    scores.push({ key: "tilfredshed", weight: 0.13, score: s });
  }

  // Omsætning pr. time (18%)
  if (revenue_per_hour != null && revenue_per_hour > 0) {
    const s = Math.min((revenue_per_hour / 295) * 100, 130);
    scores.push({ key: "kr/time", weight: 0.18, score: s });
  }

  // Fremmøde (13%)
  if (avg_fravær_pct != null) {
    const fremmøde = 100 - avg_fravær_pct;
    const s = Math.min((fremmøde / 90) * 100, 100);
    scores.push({ key: "fremmøde", weight: 0.13, score: s });
  }

  if (scores.length === 0) return { score: null, weakest: [] };

  const totalWeight = scores.reduce((a, b) => a + b.weight, 0);
  const weighted = scores.reduce((a, b) => a + b.score * b.weight, 0);
  const overall = Math.round(weighted / totalWeight);

  const weakest = scores
    .filter(s => s.score < 75)
    .sort((a, b) => a.score - b.score)
    .slice(0, 2)
    .map(s => s.key);

  return { score: overall, weakest };
}

function kpiColor(pct) {
  if (pct >= 95) return "#22C55E";
  if (pct >= 75) return "#84CC16";
  if (pct >= 50) return "#EAB308";
  if (pct >= 25) return "#F97316";
  return "#EF4444";
}

// --- 6 måneds historik boks ---
export function MonthHistoryRow({ data, overallScore }) {
  const { months_elapsed, total_months, month_labels, monthly_kpi_scores } = data;

  // Samlet KPI for hele perioden: gennemsnit af alle kendte måneder
  const knownScores = [];
  for (let i = 0; i < total_months; i++) {
    const isCurrent = i === months_elapsed - 1;
    const isFuture = i >= months_elapsed;
    if (isCurrent && overallScore != null) knownScores.push(overallScore);
    else if (!isFuture && monthly_kpi_scores?.[i] != null) knownScores.push(monthly_kpi_scores[i]);
  }
  const periodAvg = knownScores.length > 0
    ? Math.round(knownScores.reduce((a, b) => a + b, 0) / knownScores.length)
    : null;

  return (
    <Card className="bg-card border-border shadow-none mb-6">
      <CardHeader className="pb-2 pt-4">
        <CardTitle className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">
          Periodehistorik — 6 måneder
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-4">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
          {Array.from({ length: total_months }).map((_, i) => {
            const isCurrent = i === months_elapsed - 1;
            const isFuture = i >= months_elapsed;
            const label = month_labels?.[i] || `Md. ${i + 1}`;

            let pct = null;
            if (isCurrent) {
              pct = overallScore;
            } else if (!isFuture) {
              pct = monthly_kpi_scores?.[i] ?? null;
            }

            const subtext = isFuture
              ? "Ikke startet"
              : isCurrent
                ? "Nu"
                : "Afsluttet";

            return (
              <div key={i} className="flex flex-col items-center gap-1">
                <KPICircle
                  mode={isFuture ? "placeholder" : "simple"}
                  label={label}
                  centerValue={pct != null ? `${Math.round(pct)}%` : "—"}
                  percentage={isFuture ? 0 : (pct ?? 0)}
                  large={false}
                />
                <span className={`text-[12px] text-center ${isCurrent ? "text-[#FF2A6D] font-bold" : "text-muted-foreground"}`}>
                  {subtext}
                </span>
              </div>
            );
          })}
        </div>

        {/* Samlet KPI progressbar for hele perioden */}
        {periodAvg != null && (
          <div className="pt-3 border-t border-border">
            <div className="flex justify-between items-center text-[13px] mb-1.5">
              <span className="text-muted-foreground">Samlet KPI for perioden</span>
              <span className="font-bold" style={{ color: kpiColor(periodAvg) }}>{periodAvg}%</span>
            </div>
            <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${Math.min(periodAvg, 100)}%`, background: kpiColor(periodAvg) }}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --- KPI Circles row ---
export function KPICirclesRow({ data }) {
  const {
    total_6month, goal_6month, months_elapsed, total_months,
    satisfaction_score, satisfaction_count,
    booking_pct, booking_avg_period, booking_goal_period,
    revenue_per_hour, avg_fravær_pct,
    prev_booking_avg, prev_satisfaction_score,
    month_labels,
    timer_booket_pct,
  } = data;

  // Omsætning vs forventet
  let revPct = 0;
  let revDisplay = "—";
  if (goal_6month > 0 && months_elapsed > 0) {
    const expected = (months_elapsed / total_months) * goal_6month;
    revPct = expected > 0 ? (total_6month / expected) * 100 : 0;
    revDisplay = `${Math.round(revPct)}%`;
  }

  // Elevtilfredshed
  let satisfactionSubtext = "Mål: 80%";
  if (satisfaction_score != null && prev_satisfaction_score != null) {
    const diff = Math.round((satisfaction_score - prev_satisfaction_score) * 10) / 10;
    if (Math.abs(diff) >= 0.5) {
      satisfactionSubtext = diff > 0
        ? `+${diff} siden sidste periode`
        : `${diff} siden sidste periode`;
    }
  }

  // Gns. omsætning pr. time
  const RPH_GOAL = 295;
  const rphDisplay = revenue_per_hour > 0 ? fmt(revenue_per_hour) : "—";
  const rphPct = revenue_per_hour > 0 ? Math.min((revenue_per_hour / RPH_GOAL) * 100, 130) : 0;

  // Fremmøde
  const fremmødePctValue = avg_fravær_pct != null ? Math.round((100 - avg_fravær_pct) * 10) / 10 : null;
  const fremmødeDisplay = fremmødePctValue != null ? `${fremmødePctValue}%` : "—";
  const fremmødeCirclePct = fremmødePctValue != null ? Math.min((fremmødePctValue / 90) * 100, 100) : 0;

  // Overall
  const { score: overallScore, weakest } = calcOverallScore(data);

  const weakestLabels = {
    "omsætning": "omsætning",
    "booking": "booking",
    "tilfredshed": "tilfredshed",
    "kr/time": "kr/time",
    "fremmøde": "fremmøde",
    "timer": "timer booket",
  };
  const weakestText = weakest.length > 0
    ? `Trukket ned af ${weakest.map(k => weakestLabels[k]).join(" og ")}`
    : null;

  // Indeværende måned (ikke range)
  const currentMonthLabel = month_labels && months_elapsed > 0
    ? month_labels[months_elapsed - 1]
    : null;

  return (
    <Card className="bg-card border-border shadow-none mb-6">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-[#FF2A6D]" />
            <CardTitle className="text-sm font-bold tracking-tight">KPI Overblik</CardTitle>
          </div>
          {currentMonthLabel && (
            <span className="text-[13px] text-muted-foreground">{currentMonthLabel}</span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Samlet KPI */}
        <div className="flex flex-col items-center mb-6">
          <KPICircle
            mode="simple"
            label="Samlet KPI"
            centerValue={overallScore != null ? `${overallScore}%` : "—"}
            centerUnit="vægtet score"
            percentage={overallScore ?? 0}
            large={true}
          />
          {weakestText && (
            <p className="text-[12px] text-muted-foreground mt-2 text-center">{weakestText}</p>
          )}
        </div>

        {/* 4 KPI circles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-2 justify-items-center">

          <KPICircle
            mode="revenue"
            label="Omsætning"
            centerValue={revDisplay}
            centerUnit={months_elapsed > 0 ? "vs forventet" : ""}
            percentage={revPct}
            months_elapsed={months_elapsed}
            total_months={total_months}
            goalLabel={goal_6month > 0 ? `Mål: ${fmt(Math.round(goal_6month / total_months))}/md.` : undefined}
          />

          <KPICircle
            mode="simple"
            label="Elevtilfredshed"
            centerValue={satisfaction_score != null ? `${satisfaction_score}%` : "—"}
            centerUnit={satisfaction_count != null ? `${satisfaction_count} svar` : ""}
            percentage={satisfaction_score != null ? Math.min((satisfaction_score / 80) * 100, 130) : 0}
            trendCurrent={satisfaction_score}
            trendPrevious={prev_satisfaction_score}
            goalLabel="Mål: 80%"
          />

          <KPICircle
            mode="simple"
            label="Gns. kr. pr. time"
            centerValue={rphDisplay}
            centerUnit={revenue_per_hour > 0 ? "kr/time" : "ingen data"}
            percentage={rphPct}
            goalLabel={`Mål: ${RPH_GOAL} kr/time`}
          />

          <KPICircle
            mode="simple"
            label="Fremmøde"
            centerValue={fremmødeDisplay}
            centerUnit={avg_fravær_pct != null ? `fravær: ${avg_fravær_pct}%` : ""}
            percentage={fremmødeCirclePct}
            goalLabel="Mål: 90% fremmøde"
          />

        </div>
      </CardContent>
    </Card>
  );
}

// --- Period banner ---
function PeriodBanner({ data }) {
  const { city } = data;
  const cityLabel = city === "Alle" ? "Alle skoler" : displayName(city);

  return (
    <div className="flex items-baseline justify-between mb-5 flex-wrap gap-x-4 gap-y-1">
      <h2 className="text-lg font-bold text-foreground">
        {cityLabel} — KPI periode
      </h2>
      <p className="text-[13px] text-muted-foreground">
        Periode: Januar – Juni &nbsp;·&nbsp; Næste periode: Juli – December
      </p>
    </div>
  );
}

// --- Main component ---
export function ManagerKPIView({ data }) {
  if (!data) return null;

  const { score: overallScore } = calcOverallScore(data);

  return (
    <div className="animate-fade-in-up opacity-0 stagger-1">
      <PeriodBanner data={data} />
      <KPICirclesRow data={data} />
      <OmsætningBar data={data} />
      <MonthHistoryRow data={data} overallScore={overallScore} />
      <BookingBar
        booking_pct={data.booking_pct}
        booking_date={data.booking_date}
        prev_booking_avg={data.prev_booking_avg}
        booking_avg_period={data.booking_avg_period}
        booking_goal_period={data.booking_goal_period}
      />
    </div>
  );
}
